import React, { useState, useEffect } from 'react';
import { ArrowRight, Upload, Camera, LogOut, QrCode, Plus, X, Search, UserCheck, UserX, LayoutDashboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';
import { ProfileAvatar } from '../components/ProfileAvatar';

interface Assistant extends Profile {
  status: 'active' | 'not_active';
}

function UpdateProfile() {
  const navigate = useNavigate();
  const [previewUrl, setPreviewUrl] = useState('https://images.unsplash.com/photo-1600486913747-55e5470d6f40');
  const [showQRCode, setShowQRCode] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showAssistantModal, setShowAssistantModal] = useState(false);
  const [assistantSmartKey, setAssistantSmartKey] = useState('');
  const [assistantError, setAssistantError] = useState<string | null>(null);
  const [addingAssistant, setAddingAssistant] = useState(false);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile?.account_type === 'shop_owner') {
      fetchAssistants();
    }
  }, [profile]);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('لم يتم العثور على المستخدم');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile) throw new Error('لم يتم العثور على الملف الشخصي');

      setProfile(profile);
      setIsAdmin(profile.phone === '7331533033');
      
      if (profile.avatar_url) {
        const { data: { publicUrl } } = supabase
          .storage
          .from('avatars')
          .getPublicUrl(profile.avatar_url);
        setPreviewUrl(publicUrl);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err instanceof Error ? err.message : 'فشل في تحميل الملف الشخصي');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssistants = async () => {
    try {
      if (!profile?.smart_key) return;

      const { data: assistantRelations, error: relationsError } = await supabase
        .from('shop_assistants')
        .select('assistant_smart_key, status')
        .eq('owner_smart_key', profile.smart_key);

      if (relationsError) throw relationsError;

      if (!assistantRelations?.length) {
        setAssistants([]);
        return;
      }

      const { data: assistantProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('smart_key', assistantRelations.map(r => r.assistant_smart_key));

      if (profilesError) throw profilesError;

      const assistantsList = assistantProfiles.map(assistantProfile => {
        const relation = assistantRelations.find(r => r.assistant_smart_key === assistantProfile.smart_key);
        return {
          ...assistantProfile,
          status: relation?.status || 'not_active'
        };
      }) as Assistant[];

      setAssistants(assistantsList);
    } catch (err) {
      console.error('Error fetching assistants:', err);
      setError('Failed to fetch assistants');
    }
  };

  const handleAddAssistant = async () => {
    if (!assistantSmartKey || addingAssistant) return;
    
    setAssistantError(null);
    setAddingAssistant(true);

    try {
      const { data, error } = await supabase
        .rpc('add_shop_assistant', {
          p_assistant_smart_key: assistantSmartKey.toUpperCase()
        });

      if (error) throw error;

      setShowAssistantModal(false);
      setAssistantSmartKey('');
      await fetchAssistants();

    } catch (err) {
      console.error('Error adding assistant:', err);
      setAssistantError(err instanceof Error ? err.message : 'Failed to add assistant');
    } finally {
      setAddingAssistant(false);
    }
  };

  const handleUpdateAssistantStatus = async (assistantSmartKey: string, newStatus: 'active' | 'not_active') => {
    setUpdatingStatus(assistantSmartKey);
    try {
      const { error } = await supabase
        .rpc('update_assistant_status', {
          p_assistant_smart_key: assistantSmartKey,
          p_status: newStatus
        });

      if (error) throw error;
      await fetchAssistants();
    } catch (err) {
      console.error('Error updating assistant status:', err);
      setError('Failed to update assistant status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleRemoveAssistant = async (assistantSmartKey: string) => {
    try {
      const { error } = await supabase
        .rpc('remove_shop_assistant', {
          p_assistant_smart_key: assistantSmartKey
        });

      if (error) throw error;
      await fetchAssistants();
    } catch (err) {
      console.error('Error removing assistant:', err);
      setError('Failed to remove assistant');
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setError(null);

      if (!file.type.startsWith('image/')) {
        throw new Error('الرجاء تحميل ملف صورة');
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error('يجب أن يكون حجم الصورة أقل من 5 ميجابايت');
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('لم يتم العثور على المستخدم');

      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/${Date.now()}.${fileExt}`;

      if (profile?.avatar_url) {
        await supabase.storage
          .from('avatars')
          .remove([profile.avatar_url]);
      }

      const { error: uploadError, data } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
          onUploadProgress: (progress) => {
            setUploadProgress((progress.loaded / progress.total) * 100);
          },
        });

      if (uploadError) throw uploadError;
      if (!data) throw new Error('فشل التحميل - لم يتم إرجاع أي بيانات');

      const { data: { publicUrl } } = supabase
        .storage
        .from('avatars')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: filePath })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setProfile(prev => prev ? { ...prev, avatar_url: filePath } : null);
      setPreviewUrl(publicUrl);

    } catch (err) {
      console.error('Error updating profile picture:', err);
      setError(err instanceof Error ? err.message : 'فشل في تحديث صورة الملف الشخصي');
      if (profile?.avatar_url) {
        const { data: { publicUrl } } = supabase
          .storage
          .from('avatars')
          .getPublicUrl(profile.avatar_url);
        setPreviewUrl(publicUrl);
      } else {
        setPreviewUrl('https://images.unsplash.com/photo-1600486913747-55e5470d6f40');
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Error signing out:', err);
      alert('فشل في تسجيل الخروج. الرجاء المحاولة مرة أخرى.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-6 flex items-center justify-center">
        <div className="text-gray-600">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-white/50"
          >
            <ArrowRight size={24} className="text-gray-800" />
          </button>
          <h1 className="text-xl font-semibold">الملف الشخصي</h1>
        </div>
        <div className="flex items-center space-x-2">
          {isAdmin && (
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
            >
              <LayoutDashboard size={18} />
              <span>لوحة التحكم</span>
            </button>
          )}
          <button 
            onClick={handleSignOut}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-red-50 text-[#F87171] hover:bg-red-100 transition-colors"
          >
            <LogOut size={18} />
            <span>تسجيل الخروج</span>
          </button>
        </div>
      </div>

      <div className="w-full max-w-sm mx-auto bg-white rounded-3xl shadow-lg p-8">
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col items-center space-y-4 mb-8">
          <div className="relative">
            <div className="w-32 h-32 rounded-full overflow-hidden relative">
              <ProfileAvatar
                src={previewUrl}
                username={profile?.username || ''}
                size="xl"
              />
              {isUploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full border-4 border-white border-t-transparent animate-spin"></div>
                </div>
              )}
            </div>
            <label 
              htmlFor="profile-upload" 
              className={`absolute bottom-0 right-0 w-10 h-10 bg-[#F87171] rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-[#EF4444] transition-colors ${
                isUploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Camera size={20} className="text-white" />
            </label>
            <input
              type="file"
              id="profile-upload"
              accept="image/*"
              onChange={handleImageChange}
              disabled={isUploading}
              className="hidden"
            />
          </div>
          <div className="text-center">
            <h2 className="text-xl font-semibold">{profile?.username}</h2>
            <p className="text-gray-500">{profile?.phone}</p>
          </div>
        </div>

        <div className="mb-8">
          <button
            onClick={() => setShowQRCode(!showQRCode)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <QrCode className="text-gray-600" />
              <div>
                <p className="font-medium">الرمز الذكي</p>
                <p className="text-sm text-gray-500">{profile?.smart_key}</p>
              </div>
            </div>
            <span className="text-sm text-blue-500">
              {showQRCode ? 'إخفاء QR' : 'عرض QR'}
            </span>
          </button>

          {showQRCode && (
            <div className="mt-4 p-4 bg-white rounded-xl flex flex-col items-center space-y-4">
              <QRCode
                value={profile?.smart_key || ''}
                size={200}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                viewBox={`0 0 256 256`}
              />
              <p className="text-sm text-gray-500">امسح الرمز للاتصال</p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-500">نوع الحساب</p>
            <p className="font-medium">
              {profile?.account_type === 'shop_owner' ? 'صاحب متجر' : 
               profile?.account_type === 'customer' ? 'عميل' : 
               profile?.account_type === 'admin' ? 'مدير النظام' :
               'مساعد متجر'}
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-500">العملة</p>
            <p className="font-medium">{profile?.currency}</p>
          </div>
          {profile?.account_type === 'shop_owner' && (
            <>
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500">اسم المتجر</p>
                <p className="font-medium">{profile?.shop_name}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500">نوع النشاط</p>
                <div className="flex items-center justify-between">
                  <p className="font-medium">{profile?.business_type}</p>
                  <button className="text-blue-500 hover:text-blue-600">
                    تعديل
                  </button>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">مساعدو المتجر</p>
                  <button 
                    onClick={() => setShowAssistantModal(true)}
                    className="flex items-center space-x-1 text-sm text-blue-500 hover:text-blue-600"
                  >
                    <Plus size={16} />
                    <span>إضافة مساعد</span>
                  </button>
                </div>
                <div className="space-y-2">
                  {assistants.length === 0 ? (
                    <p className="text-sm text-gray-500">لم تتم إضافة مساعدين بعد</p>
                  ) : (
                    assistants.map((assistant) => (
                      <div 
                        key={assistant.smart_key}
                        className="flex items-center justify-between bg-white p-3 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <ProfileAvatar
                            username={assistant.username}
                            size="sm"
                          />
                          <div>
                            <p className="font-medium">{assistant.username}</p>
                            <p className="text-sm text-gray-500">{assistant.smart_key}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleUpdateAssistantStatus(
                              assistant.smart_key,
                              assistant.status === 'active' ? 'not_active' : 'active'
                            )}
                            disabled={updatingStatus === assistant.smart_key}
                            className={`p-2 rounded-lg ${
                              assistant.status === 'active'
                                ? 'bg-green-100 text-green-600'
                                : 'bg-gray-100 text-gray-600'
                            } hover:opacity-80 transition-opacity`}
                          >
                            {assistant.status === 'active' ? (
                              <UserCheck size={18} />
                            ) : (
                              <UserX size={18} />
                            )}
                          </button>
                          <button
                            onClick={() => handleRemoveAssistant(assistant.smart_key)}
                            className="p-2 rounded-lg bg-red-100 text-red-600 hover:opacity-80 transition-opacity"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showAssistantModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-md rounded-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">إضافة مساعد متجر</h3>
              <button 
                onClick={() => {
                  setShowAssistantModal(false);
                  setAssistantSmartKey('');
                  setAssistantError(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {assistantError && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {assistantError}
              </div>
            )}

            <div className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="أدخل الرمز الذكي للمساعد"
                  value={assistantSmartKey}
                  onChange={(e) => setAssistantSmartKey(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 pl-10 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 uppercase"
                  maxLength={6}
                />
                <Search className="absolute left-3 top-3.5 text-gray-400" size={20} />
              </div>

              <button
                onClick={handleAddAssistant}
                disabled={!assistantSmartKey || assistantSmartKey.length !== 6 || addingAssistant}
                className="w-full py-3 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingAssistant ? 'جاري إضافة المساعد...' : 'إضافة المساعد'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpdateProfile;